import { ConfigItem, WebEDIError } from './types';

/**
 * Sorts an array of WebEDI errors based on UI configuration order.
 */
export const sortArrayBasedOnConfig = (
  errors: Array<WebEDIError>,
  configuration: Array<ConfigItem>,
): Array<WebEDIError> => {
  const getConfigIndex = (segment: string): number => {
    return configuration.findIndex((config) => config.type === segment);
  };

  const getFieldWeight = (configItem: ConfigItem | undefined, path: Array<string>): number => {
    if (!configItem) {
      return Infinity;
    }
    const fields = configItem.fields || [];
    let currentFields = fields;
    let weight = 0;
    let depth = 0;

    for (const segment of path) {
      if (!isNaN(Number(segment))) {
        continue;
      } // Skip numeric indices

      const fieldIndex = currentFields.findIndex((field) => 'path' in field && field.path === segment);
      if (fieldIndex === -1) {
        return Infinity;
      }

      weight = weight * 1000 + fieldIndex;
      const field = currentFields[fieldIndex];
      if ('fields' in field && field.fields) {
        currentFields = field.fields;
        depth++;
      } else {
        break;
      }
    }

    return weight * Math.pow(1000, 10 - depth); // Adjust weight based on depth
  };

  const comparePaths = (aPath: Array<string>, bPath: Array<string>): number => {
    const aConfigIndex = getConfigIndex(aPath[0]);
    const bConfigIndex = getConfigIndex(bPath[0]);

    // If configs have different indices, sort based on index (-1 means not found)
    if (aConfigIndex !== bConfigIndex) {
      // If one exists in config and other doesn't, prioritize the one in config
      if (aConfigIndex === -1) {
        return 1;
      } // b comes before a
      if (bConfigIndex === -1) {
        return -1;
      } // a comes before b

      // Both have config indices, compare them directly
      return aConfigIndex - bConfigIndex;
    }

    // If neither path was found in the configuration, compare lexicographically
    if (aConfigIndex === -1) {
      return aPath.join('.').localeCompare(bPath.join('.'));
    }

    const configItem = configuration[aConfigIndex];

    for (let i = 1; i < Math.min(aPath.length, bPath.length); i++) {
      if (aPath[i] !== bPath[i]) {
        // If both segments are numeric, compare them
        if (!isNaN(Number(aPath[i])) && !isNaN(Number(bPath[i]))) {
          return Number(aPath[i]) - Number(bPath[i]);
        }

        // If we've reached a non-numeric difference, compare weights
        const aWeight = getFieldWeight(configItem, aPath.slice(1));
        const bWeight = getFieldWeight(configItem, bPath.slice(1));
        if (aWeight !== bWeight) {
          return aWeight - bWeight;
        }
      }
    }

    return aPath.length - bPath.length;
  };

  return [...errors].sort((a, b) => {
    const aPath = a.id.split('.');
    const bPath = b.id.split('.');
    return comparePaths(aPath, bPath);
  });
};
